import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import {
  ListarHorariosResponse,
  SalvarMarcacoesRequest,
  HorarioOficialConfig,
  DiaSemana,
} from '../types/horariosOficiais';
import {
  listarHorariosOficiais,
  salvarMarcacoes,
  limparMarcacoes,
  obterConfiguracoes,
} from '../services/horariosOficiaisService';

export function useHorariosOficiais() {
  const { enqueueSnackbar } = useSnackbar();
  const [dados, setDados] = useState<ListarHorariosResponse | null>(null);
  const [configuracoes, setConfiguracoes] = useState<HorarioOficialConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  /**
   * Carrega os horários de todos os oficiais
   */
  const carregarHorarios = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarHorariosOficiais();
      setDados(response);
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.message || 'Erro ao carregar horários',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  /**
   * Carrega as configurações do sistema
   */
  const carregarConfiguracoes = useCallback(async () => {
    try {
      const configs = await obterConfiguracoes();
      setConfiguracoes(configs);
    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error);
    }
  }, []);

  /**
   * Salva as marcações de um dia específico
   */
  const salvar = useCallback(
    async (data: SalvarMarcacoesRequest) => {
      setSaving(true);
      try {
        await salvarMarcacoes(data);
        enqueueSnackbar('Horários salvos com sucesso!', { variant: 'success' });
        await carregarHorarios(); // Recarrega para atualizar a tela
        return true;
      } catch (error: any) {
        enqueueSnackbar(
          error?.response?.data?.message || 'Erro ao salvar horários',
          { variant: 'error' }
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [enqueueSnackbar, carregarHorarios]
  );

  /**
   * Limpa todas as marcações de um dia
   */
  const limpar = useCallback(
    async (dataReferencia: string) => {
      setSaving(true);
      try {
        await limparMarcacoes(dataReferencia);
        enqueueSnackbar('Marcações removidas com sucesso!', { variant: 'success' });
        await carregarHorarios();
        return true;
      } catch (error: any) {
        enqueueSnackbar(
          error?.response?.data?.message || 'Erro ao limpar marcações',
          { variant: 'error' }
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
    [enqueueSnackbar, carregarHorarios]
  );

  /**
   * Verifica se um dia está bloqueado para edição
   */
  const isDiaBloqueado = useCallback(
    (dataReferencia: string): boolean => {
      return dados?.diasBloqueados.includes(dataReferencia) || false;
    },
    [dados]
  );

  /**
   * Obtém o valor de uma configuração específica
   */
  const getConfig = useCallback(
    (chave: string): string | null => {
      const config = configuracoes.find(c => c.chave === chave);
      return config?.valor || null;
    },
    [configuracoes]
  );

  /**
   * Obtém as marcações de um oficial específico para um dia
   */
  const getMarcacao = useCallback(
    (idMilitar: string, diaSemana: DiaSemana) => {
      const oficial = dados?.oficiais.find(o => o.id_militar === idMilitar);
      return oficial?.marcacoes[diaSemana] || null;
    },
    [dados]
  );

  // Carrega dados iniciais ao montar
  useEffect(() => {
    carregarHorarios();
    carregarConfiguracoes();
  }, [carregarHorarios, carregarConfiguracoes]);

  return {
    dados,
    configuracoes,
    loading,
    saving,
    carregarHorarios,
    carregarConfiguracoes,
    salvar,
    limpar,
    isDiaBloqueado,
    getConfig,
    getMarcacao,
  };
}
